/* START_MODULE_CONTRACT
 * PURPOSE: Frontend UI module: AdminUsersPage.
 * SCOPE: React component or page.
 * DEPENDS: M-FE-API, M-FE-AUTH
 * LINKS: M-FE-ADMIN
 * END_MODULE_CONTRACT
 *
 * START_MODULE_MAP
 * AdminUsersPage - Frontend UI module: AdminUsersPage.
 * END_MODULE_MAP
 *
 * START_CHANGE_SUMMARY
 * LAST_CHANGE: 2026-07-02 - GRACE coverage: контракты функций, блоки/логи загрузки; закрыт метод fetchUsers и обёрнут refetch.
 * END_CHANGE_SUMMARY
 */

import { useCallback, useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  getAdminUsers,
  createAdminUser,
  updateAdminUser,
  deleteAdminUser,
} from '../../api/adminApi';
import CreateUserDialog from './components/CreateUserDialog';
import EditUserDialog from './components/EditUserDialog';
import DeleteUserDialog from './components/DeleteUserDialog';

/* START_METHOD_formatDate */
/* START_CONTRACT: formatDate
 * PURPOSE: Форматировать ISO-дату в строку DD.MM.YYYY (пустая строка при отсутствии).
 * INPUTS: { dateStr: string|null }.
 * OUTPUTS: { string }.
 * SIDE_EFFECTS: нет.
 * LINKS: M-FE-ADMIN
 * END_CONTRACT: formatDate
 */
function formatDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  return `${day}.${month}.${year}`;
}
/* END_METHOD_formatDate */

/* START_METHOD_AdminUsersPage */
/* START_CONTRACT: AdminUsersPage
 * PURPOSE: Страница администрирования пользователей: список, создание, редактирование, удаление.
 * INPUTS: {}.
 * OUTPUTS: { JSX.Element - таблица пользователей с диалогами }.
 * SIDE_EFFECTS: HTTP getAdminUsers/createAdminUser/updateAdminUser/deleteAdminUser.
 * LINKS: M-FE-ADMIN, M-FE-API
 * END_CONTRACT: AdminUsersPage
 */
export default function AdminUsersPage() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deletingUser, setDeletingUser] = useState(null);

  /* START_METHOD_fetchUsers */
  /* START_CONTRACT: fetchUsers
   * PURPOSE: Загрузить список пользователей; при ошибке (не отмена) показать сообщение.
   * INPUTS: { signal?: AbortSignal }.
   * OUTPUTS: { Promise<void> }.
   * SIDE_EFFECTS: HTTP getAdminUsers; setUsers/setError/setLoading.
   * LINKS: M-FE-ADMIN
   * END_CONTRACT: fetchUsers
   */
  const fetchUsers = useCallback(async (signal) => {
    try {
      const { data } = await getAdminUsers(signal);
      /* START_BLOCK_LOAD_OK */
      setUsers(data);
      setError('');
      /* END_BLOCK_LOAD_OK */
    } catch (err) {
      /* START_BLOCK_LOAD_FAIL */
      if (signal?.aborted) return;
      console.warn('[AdminUsersPage][fetchUsers][LOAD_FAIL] users load failed', {
        status: err.response?.status,
      });
      setError(err.response?.data?.message || 'Ошибка загрузки пользователей');
      /* END_BLOCK_LOAD_FAIL */
    } finally {
      if (!signal?.aborted) {
        setLoading(false);
      }
    }
  }, []);
  /* END_METHOD_fetchUsers */

  useEffect(() => {
    const controller = new AbortController();
    fetchUsers(controller.signal);
    return () => controller.abort();
  }, [fetchUsers]);

  /* START_METHOD_refetch */
  /* START_CONTRACT: refetch
   * PURPOSE: Перезагрузить список пользователей без AbortSignal (после мутаций).
   * INPUTS: {}.
   * OUTPUTS: { Promise<void> }.
   * SIDE_EFFECTS: делегирует fetchUsers (HTTP).
   * LINKS: M-FE-ADMIN
   * END_CONTRACT: refetch
   */
  const refetch = () => fetchUsers();
  /* END_METHOD_refetch */

  /* START_METHOD_handleCreate */
  /* START_CONTRACT: handleCreate
   * PURPOSE: Создать пользователя и обновить список.
   * INPUTS: { payload: object }.
   * OUTPUTS: { Promise<void> }.
   * SIDE_EFFECTS: HTTP createAdminUser; refetch.
   * LINKS: M-FE-ADMIN
   * END_CONTRACT: handleCreate
   */
  const handleCreate = async (payload) => {
    await createAdminUser(payload);
    await refetch();
  };
  /* END_METHOD_handleCreate */

  /* START_METHOD_handleUpdate */
  /* START_CONTRACT: handleUpdate
   * PURPOSE: Обновить пользователя по id и обновить список.
   * INPUTS: { id: number, payload: object }.
   * OUTPUTS: { Promise<void> }.
   * SIDE_EFFECTS: HTTP updateAdminUser; refetch.
   * LINKS: M-FE-ADMIN
   * END_CONTRACT: handleUpdate
   */
  const handleUpdate = async (id, payload) => {
    await updateAdminUser(id, payload);
    await refetch();
  };
  /* END_METHOD_handleUpdate */

  /* START_METHOD_handleDelete */
  /* START_CONTRACT: handleDelete
   * PURPOSE: Удалить пользователя по id и обновить список.
   * INPUTS: { id: number }.
   * OUTPUTS: { Promise<void> }.
   * SIDE_EFFECTS: HTTP deleteAdminUser; refetch.
   * LINKS: M-FE-ADMIN
   * END_CONTRACT: handleDelete
   */
  const handleDelete = async (id) => {
    await deleteAdminUser(id);
    await refetch();
  };
  /* END_METHOD_handleDelete */

  /* START_METHOD_handleEditClick */
  /* START_CONTRACT: handleEditClick
   * PURPOSE: Открыть диалог редактирования для выбранного пользователя.
   * INPUTS: { user: object }.
   * OUTPUTS: { void }.
   * SIDE_EFFECTS: setEditingUser/setEditOpen.
   * LINKS: M-FE-ADMIN
   * END_CONTRACT: handleEditClick
   */
  const handleEditClick = (user) => {
    setEditingUser(user);
    setEditOpen(true);
  };
  /* END_METHOD_handleEditClick */

  /* START_METHOD_handleDeleteClick */
  /* START_CONTRACT: handleDeleteClick
   * PURPOSE: Открыть диалог удаления для выбранного пользователя.
   * INPUTS: { user: object }.
   * OUTPUTS: { void }.
   * SIDE_EFFECTS: setDeletingUser/setDeleteOpen.
   * LINKS: M-FE-ADMIN
   * END_CONTRACT: handleDeleteClick
   */
  const handleDeleteClick = (user) => {
    setDeletingUser(user);
    setDeleteOpen(true);
  };
  /* END_METHOD_handleDeleteClick */

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h5">Управление пользователями</Typography>
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => setCreateOpen(true)}
        >
          Создать пользователя
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {users.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          Пользователи не найдены
        </Typography>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Статус</TableCell>
                <TableCell>Роль</TableCell>
                <TableCell>Дата создания</TableCell>
                <TableCell align="right">Действия</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((u) => (
                <TableRow key={u.id}>
                  <TableCell>{u.id}</TableCell>
                  <TableCell>{u.email}</TableCell>
                  <TableCell>{u.status}</TableCell>
                  <TableCell>{u.role}</TableCell>
                  <TableCell>{formatDate(u.createdAt)}</TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => handleEditClick(u)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleDeleteClick(u)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <CreateUserDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSubmit={handleCreate}
      />

      <EditUserDialog
        open={editOpen}
        onClose={() => {
          setEditOpen(false);
          setEditingUser(null);
        }}
        onUpdate={handleUpdate}
        user={editingUser}
      />

      <DeleteUserDialog
        open={deleteOpen}
        onClose={() => {
          setDeleteOpen(false);
          setDeletingUser(null);
        }}
        onConfirm={handleDelete}
        user={deletingUser}
      />
    </Box>
  );
}
/* END_METHOD_AdminUsersPage */
